import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge } from 'reactstrap';
import { get, put, post } from 'helpers/api_helper';
import { APPOINTMENT_URL, TREATMENT_URL, BILLING_URL } from 'helpers/url_helper';
import { showSuccessAlert, showErroeAlert } from 'pages/utils/alertMessages';
import cookieHelper from 'helpers/getCookieData';
import moment from 'moment';
import {
    RiTimeLine,
    RiHeartPulseLine,
    RiUserReceivedLine,
    RiStethoscopeLine,
    RiStopCircleLine,
    RiCheckboxCircleLine,
    RiFileTextLine,
    RiHospitalLine,
    RiBillLine,
    RiMoneyDollarCircleLine,
    RiHandCoinLine,
    RiCheckLine,
    RiMapPinAddFill
} from 'react-icons/ri';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { encodePatientId } from 'helpers/url_crypto_helper';
import { isPaymentModuleEnabled } from 'helpers/tenantModuleHelper';

const LiveTreatmentBanner = ({ patientData, apptId = null, onSessionUpdated = () => { } }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [activeAppt, setActiveAppt] = useState(null);
    const [sessionInvoice, setSessionInvoice] = useState(null);
    const [isEndModalOpen, setIsEndModalOpen] = useState(false);
    const [elapsedTime, setElapsedTime] = useState('00:00:00');
    const [saving, setSaving] = useState(false);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const isSubmittingRef = useRef(false);

    // Quick Invoice Modal State
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [invoiceModalData, setInvoiceModalData] = useState({
        treatment_name: '',
        cost: '',
        discount: 0,
        paid: '',
        payment_mode: 'Cash',
        remark: '',
        treatment_id: null
    });

    // Completion form state
    const [completionData, setCompletionData] = useState({
        treatment_given: '',
        tooths: '',
        clinical_remarks: '',
        treatment_cost: '',
        next_visit: ''
    });

    const checkSessionInvoice = async (appt) => {
        if (!appt?.id || !patientData?.id) {
            setSessionInvoice(null);
            return;
        }
        try {
            const billRes = await get(`${BILLING_URL}?patient_id=${patientData.id}&limit=all`);
            const bills = billRes?.body?.items || (Array.isArray(billRes?.body) ? billRes.body : (Array.isArray(billRes?.items) ? billRes.items : (Array.isArray(billRes) ? billRes : [])));
            const sessionRefStr = `Session #${appt.id}`;
            const found = bills.find(b => {
                if (b.status === 3) return false;
                const infoStr = typeof b.treatment_info === 'string' ? b.treatment_info : JSON.stringify(b.treatment_info || '');
                const noteStr = String(b.note || '');
                const remarkStr = String(b.remark || '');
                return infoStr.includes(sessionRefStr) || noteStr.includes(sessionRefStr) || remarkStr.includes(sessionRefStr);
            });
            setSessionInvoice(found || null);
        } catch (err) {
            console.error("Error checking session invoice:", err);
        }
    };

    const fetchActiveSession = async () => {
        if (!patientData?.id) return;
        try {
            // If specific apptId is provided in URL
            if (apptId) {
                const res = await get(`${APPOINTMENT_URL}/${apptId}`);
                if (res?.success && res?.body) {
                    setActiveAppt(res.body);
                    checkSessionInvoice(res.body);
                    return;
                }
            }

            // Otherwise search latest appointments for today
            const res = await get(`${APPOINTMENT_URL}?patient_id=${patientData.id}&limit=5`);
            if (res?.success && res?.body?.items?.length > 0) {
                // Find in-progress appointment (attended but not completed, or arrived today)
                const inProgress = res.body.items.find(a =>
                    a.status === 0 && a.attened_time && !a.completed_time
                ) || res.body.items.find(a =>
                    a.status === 0 && a.arravel_time && !a.completed_time
                );

                if (inProgress) {
                    setActiveAppt(inProgress);
                    checkSessionInvoice(inProgress);
                } else {
                    // Check if there was an appointment completed in the last 2 hours to show summary
                    const recentDone = res.body.items.find(a =>
                        a.completed_time && moment().diff(moment(a.completed_time), 'hours') < 2
                    );
                    setActiveAppt(recentDone || null);
                    if (recentDone) {
                        checkSessionInvoice(recentDone);
                    } else {
                        setSessionInvoice(null);
                    }
                }
            }
        } catch (e) {
            console.error("Error loading active session:", e);
        }
    };

    useEffect(() => {
        fetchActiveSession();
    }, [patientData?.id, apptId]);

    // Live Stopwatch calculation
    useEffect(() => {
        if (!activeAppt?.attened_time || activeAppt?.completed_time) {
            if (activeAppt?.attened_time && activeAppt?.completed_time) {
                const totalSeconds = moment(activeAppt.completed_time).diff(moment(activeAppt.attened_time), 'seconds');
                const duration = moment.duration(totalSeconds, 'seconds');
                const hours = String(Math.floor(duration.asHours())).padStart(2, '0');
                const mins = String(duration.minutes()).padStart(2, '0');
                const secs = String(duration.seconds()).padStart(2, '0');
                setElapsedTime(`${hours}:${mins}:${secs}`);
            }
            return;
        }

        const interval = setInterval(() => {
            const start = moment(activeAppt.attened_time);
            const totalSeconds = moment().diff(start, 'seconds');
            const duration = moment.duration(totalSeconds, 'seconds');
            const hours = String(Math.floor(duration.asHours())).padStart(2, '0');
            const mins = String(duration.minutes()).padStart(2, '0');
            const secs = String(duration.seconds()).padStart(2, '0');
            setElapsedTime(`${hours}:${mins}:${secs}`);
        }, 1000);

        return () => clearInterval(interval);
    }, [activeAppt?.attened_time, activeAppt?.completed_time]);

    if (!activeAppt || (!activeAppt.arravel_time && !activeAppt.attened_time)) {
        return null;
    }

    const isInProgress = Boolean(activeAppt.attened_time && !activeAppt.completed_time);
    const isCheckedInOnly = Boolean(activeAppt.arravel_time && !activeAppt.attened_time);
    const isCompleted = Boolean(activeAppt.completed_time);

    const handleStartFromBanner = async () => {
        try {
            const nowIso = new Date().toISOString();
            const res = await put(`${APPOINTMENT_URL}/${activeAppt.id}`, {
                attened_time: nowIso,
                is_visited: 1
            });
            if (res?.success) {
                showSuccessAlert("Treatment Session Started!");
                fetchActiveSession();
                onSessionUpdated();
            }
        } catch (e) {
            showErroeAlert("Failed to start session.");
        }
    };

    const handleEndSessionSubmit = async (proceedToBilling = false) => {
        if (!completionData.treatment_given.trim()) {
            showErroeAlert("Please enter what treatment was performed / given.");
            return;
        }

        setSaving(true);
        try {
            const endIso = new Date().toISOString();

            // 1. Update appointment record with completed_time and notes
            const apptNotes = `[Treatment Done]: ${completionData.treatment_given} | [Teeth]: ${completionData.tooths || 'N/A'} | [Remarks]: ${completionData.clinical_remarks || 'None'} ${activeAppt.notes ? `\nInitial Note: ${activeAppt.notes}` : ''}`;

            await put(`${APPOINTMENT_URL}/${activeAppt.id}`, {
                completed_time: endIso,
                is_visited: 1,
                notes: apptNotes,
                tretment_code: completionData.treatment_given
            });

            // 2. Automatically log treatment entry in Treatments table
            const costVal = parseFloat(completionData.treatment_cost) || 0;
            await post(TREATMENT_URL, {
                _q: [{
                    patient_id: patientData.id,
                    doctor_code: activeAppt.doctor_code || patientData.doctor,
                    treatment_date: endIso,
                    tooths: completionData.tooths || '',
                    treatment_type: completionData.treatment_given,
                    treatment_cost: costVal,
                    tretment_note: completionData.clinical_remarks || 'Completed in chair session',
                    treatment_discount: 0,
                    treatment_total_cost: costVal,
                    treatment_discount_type: 0,
                    treatment_status: 'completed'
                }]
            });

            showSuccessAlert("Treatment Session completed and logged successfully!");
            setIsEndModalOpen(false);
            setSaving(false);
            fetchActiveSession();
            onSessionUpdated();
            window.dispatchEvent(new CustomEvent('session_logs_updated', { detail: { patient_id: patientData.id } }));

            if (proceedToBilling) {
                history.push(`/patient?_p=${encodePatientId(patientData.id)}&_t=Bills`);
            }
        } catch (e) {
            setSaving(false);
            showErroeAlert("Failed to complete treatment session: " + e.message);
        }
    };

    // Auto Invoice Generation / Missing Amount Modal Trigger
    const handleGenerateInvoiceClick = async () => {
        if (!patientData?.id || isSubmittingRef.current || generatingInvoice) return;
        isSubmittingRef.current = true;
        setGeneratingInvoice(true);
        try {
            // First check if an invoice already exists for this chair session
            if (activeAppt?.id) {
                const billRes = await get(`${BILLING_URL}?patient_id=${patientData.id}&limit=all`);
                const bills = billRes?.body?.items || (Array.isArray(billRes?.body) ? billRes.body : (Array.isArray(billRes?.items) ? billRes.items : (Array.isArray(billRes) ? billRes : [])));
                const sessionRefStr = `Session #${activeAppt.id}`;
                const existingBill = bills.find(b => {
                    if (b.status === 3) return false;
                    const infoStr = typeof b.treatment_info === 'string' ? b.treatment_info : JSON.stringify(b.treatment_info || '');
                    const noteStr = String(b.note || '');
                    const remarkStr = String(b.remark || '');
                    return infoStr.includes(sessionRefStr) || noteStr.includes(sessionRefStr) || remarkStr.includes(sessionRefStr);
                });

                if (existingBill) {
                    setSessionInvoice(existingBill);
                    showSuccessAlert(t(`Invoice ${existingBill.billing_no} already exists for this session.`));
                    return;
                }
            }

            // Fetch patient's treatments to find UNBILLED treatment with cost
            const treatRes = await get(`${TREATMENT_URL}?patient_id=${patientData.id}&limit=20`);
            const items = treatRes?.body?.items || (Array.isArray(treatRes?.body) ? treatRes.body : (Array.isArray(treatRes) ? treatRes : []));

            // CRITICAL BUG FIX: ONLY consider UNBILLED treatments (!t.is_billed)
            // Never fall back to already-billed treatments!
            const unbilledItems = items.filter(t => !t.is_billed);

            // Look for unbilled treatment matching this session or unbilled treatment with cost > 0
            const matchedTreatment = unbilledItems.find(t =>
                parseFloat(t.treatment_cost || t.treatment_total_cost || 0) > 0 &&
                (t.treatment_type === completionData.treatment_given || t.treatment_type === activeAppt?.tretment_code)
            ) || unbilledItems.find(t => parseFloat(t.treatment_cost || t.treatment_total_cost || 0) > 0);

            const costVal = parseFloat(matchedTreatment?.treatment_total_cost || matchedTreatment?.treatment_cost || 0);

            // If an unbilled treatment with cost > 0 exists, generate invoice automatically!
            if (matchedTreatment && costVal > 0) {
                const cookieClinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
                const clinicId = cookieClinic?.id || 8;
                const treatmentName = matchedTreatment?.treatment_type || 'Dental Chair Consultation & Treatment';
                const discountVal = parseFloat(matchedTreatment?.treatment_discount || 0);
                const grandTotal = Math.max(0, costVal - discountVal);

                const payload = {
                    patient_id: patientData.id,
                    clinic_id: clinicId,
                    date: new Date().toISOString(),
                    items: [{
                        treatment_id: matchedTreatment.id,
                        treatment_name: treatmentName,
                        cost: costVal,
                        discount: discountVal,
                        total: grandTotal,
                        note: `Session #${activeAppt?.id || ''}`
                    }],
                    total: grandTotal,
                    cost: costVal,
                    discount: discountVal,
                    paid: 0,
                    payment_mode: 'Cash',
                    remark: `Invoice generated for chair session #${activeAppt?.id || ''}`,
                    note: `Session #${activeAppt?.id || ''}`,
                    treatment_ids: String(matchedTreatment.id)
                };

                const res = await post(BILLING_URL, payload);
                if (res?.success || res?.id || res?.billing_no || res?.body) {
                    showSuccessAlert(t("Invoice automatically generated successfully!"));
                    const createdBill = res.body || res;
                    setSessionInvoice(createdBill);
                    onSessionUpdated();
                    history.push(`/patient?_p=${encodePatientId(patientData.id)}&_t=Bills`);
                } else {
                    showErroeAlert(res?.message || res?.error || t("Failed to auto-generate invoice."));
                }
            } else {
                // Unbilled treatment has 0 cost or no unbilled treatment exists -> Open modal for user input!
                const defaultName = completionData.treatment_given || activeAppt?.tretment_code || matchedTreatment?.treatment_type || 'Dental Chair Consultation & Treatment';
                setInvoiceModalData({
                    treatment_name: defaultName,
                    cost: '',
                    discount: 0,
                    paid: '',
                    payment_mode: 'Cash',
                    remark: `Chair Session #${activeAppt?.id || ''} - Operatory Chair ${activeAppt?.chair_code || '1'}`,
                    treatment_id: matchedTreatment?.id || null
                });
                setIsInvoiceModalOpen(true);
            }
        } catch (err) {
            console.error("Error evaluating session invoice:", err);
            showErroeAlert(t("Error checking session invoice: ") + err.message);
        } finally {
            setGeneratingInvoice(false);
            isSubmittingRef.current = false;
        }
    };

    // Save invoice from modal when amount was missing
    const handleSaveModalInvoice = async (e) => {
        if (e) e.preventDefault();
        if (isSubmittingRef.current || saving) return;

        const costNum = parseFloat(invoiceModalData.cost) || 0;
        if (costNum <= 0) {
            showErroeAlert(t("Please enter a valid procedure amount / cost."));
            return;
        }

        const discountNum = parseFloat(invoiceModalData.discount) || 0;
        const grandTotal = Math.max(0, costNum - discountNum);
        const paidNum = parseFloat(invoiceModalData.paid) || 0;

        if (paidNum > grandTotal) {
            showErroeAlert(t("Paid amount cannot exceed total invoice amount."));
            return;
        }

        isSubmittingRef.current = true;
        setSaving(true);
        try {
            // First check if an invoice already exists for this chair session
            if (activeAppt?.id) {
                const billRes = await get(`${BILLING_URL}?patient_id=${patientData.id}&limit=all`);
                const bills = billRes?.body?.items || (Array.isArray(billRes?.body) ? billRes.body : (Array.isArray(billRes?.items) ? billRes.items : (Array.isArray(billRes) ? billRes : [])));
                const sessionRefStr = `Session #${activeAppt.id}`;
                const existingBill = bills.find(b => {
                    if (b.status === 3) return false;
                    const infoStr = typeof b.treatment_info === 'string' ? b.treatment_info : JSON.stringify(b.treatment_info || '');
                    const noteStr = String(b.note || '');
                    const remarkStr = String(b.remark || '');
                    return infoStr.includes(sessionRefStr) || noteStr.includes(sessionRefStr) || remarkStr.includes(sessionRefStr);
                });

                if (existingBill) {
                    setSessionInvoice(existingBill);
                    setIsInvoiceModalOpen(false);
                    showSuccessAlert(t(`Invoice ${existingBill.billing_no} already exists for this session.`));
                    return;
                }
            }

            const cookieClinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
            const clinicId = cookieClinic?.id || 8;

            let treatmentId = invoiceModalData.treatment_id;

            // If no treatment record was linked, create treatment record first
            if (!treatmentId) {
                try {
                    const treatCreateRes = await post(TREATMENT_URL, {
                        _q: [{
                            patient_id: patientData.id,
                            doctor_code: activeAppt?.doctor_code || patientData.doctor,
                            treatment_date: new Date().toISOString(),
                            tooths: '',
                            treatment_type: invoiceModalData.treatment_name || 'Dental Consultation & Treatment',
                            treatment_cost: costNum,
                            tretment_note: invoiceModalData.remark || `Logged from chair session #${activeAppt?.id || ''}`,
                            treatment_discount: discountNum,
                            treatment_total_cost: grandTotal,
                            treatment_discount_type: 0,
                            treatment_status: 'completed'
                        }]
                    });
                    if (treatCreateRes?.success && Array.isArray(treatCreateRes.body) && treatCreateRes.body[0]?.id) {
                        treatmentId = treatCreateRes.body[0].id;
                    }
                } catch (trErr) {
                    console.warn("Auto treatment logging notice:", trErr);
                }
            }

            const payload = {
                patient_id: patientData.id,
                clinic_id: clinicId,
                date: new Date().toISOString(),
                items: [{
                    treatment_id: treatmentId || 0,
                    treatment_name: invoiceModalData.treatment_name || 'Dental Procedure',
                    cost: costNum,
                    discount: discountNum,
                    total: grandTotal,
                    note: `Session #${activeAppt?.id || ''}`
                }],
                total: grandTotal,
                cost: costNum,
                discount: discountNum,
                paid: paidNum,
                payment_mode: invoiceModalData.payment_mode || 'Cash',
                remark: invoiceModalData.remark || `Invoice generated for chair session #${activeAppt?.id || ''}`,
                note: `Session #${activeAppt?.id || ''}`,
                treatment_ids: treatmentId ? String(treatmentId) : '0'
            };

            const res = await post(BILLING_URL, payload);
            if (res?.success || res?.id || res?.billing_no || res?.body) {
                showSuccessAlert(t("Invoice generated successfully!"));
                const createdBill = res.body || res;
                setSessionInvoice(createdBill);
                setIsInvoiceModalOpen(false);
                onSessionUpdated();
                history.push(`/patient?_p=${encodePatientId(patientData.id)}&_t=Bills`);
            } else {
                showErroeAlert(res?.message || res?.error || t("Failed to generate bill."));
            }
        } catch (err) {
            console.error("Error creating invoice from modal:", err);
            showErroeAlert(t("Error creating invoice: ") + err.message);
        } finally {
            setSaving(false);
            isSubmittingRef.current = false;
        }
    };

    return (
        <>
            <div className={`live-session-banner mb-3 p-3 rounded-3 border ${isInProgress
                ? 'bg-primary-subtle border-primary'
                : isCheckedInOnly
                    ? 'bg-warning-subtle border-warning'
                    : 'bg-success-subtle border-success'
                }`}>
                <Row className="g-2 align-items-center">
                    {/* Left Status & Chair info */}
                    <Col lg={4} md={6}>
                        <div className="d-flex align-items-center gap-2.5">
                            <div className={`session-indicator-icon p-2 rounded-circle ${isInProgress ? 'bg-primary text-white' : isCheckedInOnly ? 'bg-warning text-dark' : 'bg-success text-white'
                                }`}>
                                {isInProgress ? <RiHeartPulseLine className="font-size-20" /> : isCheckedInOnly ? <RiUserReceivedLine className="font-size-20" /> : <RiCheckboxCircleLine className="font-size-20" />}
                            </div>
                            <div className='ms-2'>
                                <div className="d-flex align-items-center gap-2">
                                    <h6 className="mb-0 font-weight-bold">
                                        {isInProgress ? 'Live Dental Chair Session' : isCheckedInOnly ? 'Patient Checked In (Waiting)' : 'Session Completed'}
                                    </h6>
                                    {isInProgress && (
                                        <span className="badge bg-danger pulse-badge font-size-10 px-2 py-0.5">
                                            LIVE
                                        </span>
                                    )}
                                </div>
                                <div className="text-muted font-size-12 mt-0.5 d-flex align-items-center gap-2">
                                    <span><RiHospitalLine /> Operatory Chair {activeAppt.chair_code || '1'}</span>
                                    <span>•</span>
                                    <span><RiStethoscopeLine /> {activeAppt.doctor?.name || 'Assigned Specialist'}</span>
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Center: Timestamps & Stopwatch */}
                    <Col lg={5} md={6}>
                        <div className="d-flex align-items-center justify-content-lg-center gap-3 font-size-13">
                            <div className="text-center">
                                <div className="text-muted font-size-11 text-uppercase font-weight-semibold">In Time</div>
                                <div className="font-weight-bold text-slate-800">
                                    {activeAppt.arravel_time ? moment(activeAppt.arravel_time).format('hh:mm A') : '--:--'}
                                </div>
                            </div>

                            <span className="text-muted">➔</span>

                            <div className="text-center">
                                <div className="text-muted font-size-11 text-uppercase font-weight-semibold">Start Time</div>
                                <div className="font-weight-bold text-primary">
                                    {activeAppt.attened_time ? moment(activeAppt.attened_time).format('hh:mm A') : '--:--'}
                                </div>
                            </div>

                            <span className="text-muted">➔</span>

                            <div className="text-center px-2 py-1 bg-white rounded border shadow-xs">
                                <div className="text-muted font-size-11 text-uppercase font-weight-semibold d-flex align-items-center gap-1 justify-content-center">
                                    <RiTimeLine className={isInProgress ? 'text-danger' : 'text-success'} />
                                    {isCompleted ? 'Total Duration' : 'Elapsed Time'}
                                </div>
                                <div className={`font-weight-bold font-monospace font-size-15 ${isInProgress ? 'text-danger' : 'text-success'}`}>
                                    {elapsedTime}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Right: Controller Action Buttons */}
                    <Col lg={3} md={12} className="text-lg-end">
                        {isCheckedInOnly && (
                            <Button
                                color="primary"
                                size="sm"
                                className="font-weight-semibold d-inline-flex align-items-center gap-1.5 shadow-sm"
                                onClick={handleStartFromBanner}
                            >
                                <RiHeartPulseLine /> Start Treatment Now
                            </Button>
                        )}

                        {isInProgress && (
                            <Button
                                color="danger"
                                size="sm"
                                className="font-weight-semibold d-inline-flex align-items-center gap-1.5 shadow-sm"
                                onClick={() => setIsEndModalOpen(true)}
                            >
                                <RiStopCircleLine className="font-size-16" /> End Treatment / Log Session
                            </Button>
                        )}

                        {isCompleted && isPaymentModuleEnabled() && (
                            sessionInvoice ? (
                                <div className="d-flex align-items-center justify-content-lg-end gap-1.5 flex-wrap">
                                    <span className="badge bg-success-subtle text-success border border-success font-size-12 px-2.5 py-1.5 d-inline-flex align-items-center gap-1 font-weight-semibold shadow-2xs">
                                        <RiCheckboxCircleLine className="font-size-14" /> {t("Invoice Generated")} ({sessionInvoice.billing_no})
                                    </span>
                                    <Link
                                        to={`/patient?_p=${encodePatientId(patientData.id)}&_t=Bills`}
                                        className="ms-1 btn btn-sm btn-outline-success font-size-11 py-1 px-2.5 font-weight-semibold d-inline-flex align-items-center gap-1 shadow-2xs me-1"
                                        title={t("View Invoice in Bills Tab")}
                                    >
                                        <RiFileTextLine /> {t("View Bill")}
                                    </Link>
                                </div>
                            ) : (
                                <Button
                                    color="outline-primary"
                                    size="sm"
                                    className="font-weight-semibold d-inline-flex align-items-center gap-1.5 shadow-sm"
                                    onClick={handleGenerateInvoiceClick}
                                    disabled={generatingInvoice}
                                >
                                    <RiFileTextLine /> {generatingInvoice ? t('Checking...') : t('Generate Invoice for Session')}
                                </Button>
                            )
                        )}
                    </Col>
                </Row>
            </div>

            {/* Session Completion & Treatment Log Modal */}
            <Modal isOpen={isEndModalOpen} toggle={() => setIsEndModalOpen(false)} centered size="lg">
                <ModalHeader toggle={() => setIsEndModalOpen(false)} className="bg-light">
                    <div className="d-flex align-items-center gap-2">
                        <RiCheckboxCircleLine className="text-primary font-size-20" />
                        <span className="font-weight-bold">Complete Chair Session & Log Treatment</span>
                    </div>
                </ModalHeader>

                <ModalBody className="p-4">
                    {/* Session Summary Pill */}
                    <div className="bg-light p-3 rounded mb-3 border d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                            <span className="text-muted font-size-12">{t("Patient")}:</span> <strong>{patientData?.first_name} {patientData?.last_name} ({patientData?.case_no})</strong>
                        </div>
                        <div>
                            <span className="text-muted font-size-12">{t("Doctor")}:</span> <strong>{activeAppt?.doctor?.name || activeAppt?.doctor_code || t('Assigned Doctor')}</strong>
                        </div>
                    </div>

                    <Row className="g-3">
                        <Col md={8}>
                            <label className="form-label font-weight-semibold">{t("Treatment / Procedure Delivered *")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("e.g. Ultrasonic Scaling & Polishing, Class II Composite Filling, RCT Access Prep")}
                                value={completionData.treatment_given}
                                onChange={(e) => setCompletionData({ ...completionData, treatment_given: e.target.value })}
                                autoFocus
                            />
                        </Col>

                        <Col md={4}>
                            <label className="form-label font-weight-semibold">{t("Tooth Number(s)")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("e.g. 16, 17, 36 or Full Mouth")}
                                value={completionData.tooths}
                                onChange={(e) => setCompletionData({ ...completionData, tooths: e.target.value })}
                            />
                        </Col>

                        <Col md={12}>
                            <label className="form-label font-weight-semibold">{t("Clinical Session Remarks & Procedure Notes")}</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                placeholder={t("Enter clinical observations, materials used (e.g. 3M Filtek Z350, local anesthesia 2% lignocaine with adrenaline), patient response, post-op advice...")}
                                value={completionData.clinical_remarks}
                                onChange={(e) => setCompletionData({ ...completionData, clinical_remarks: e.target.value })}
                            />
                        </Col>

                        {isPaymentModuleEnabled() && (
                            <Col md={6}>
                                <label className="form-label font-weight-semibold">{t("Estimated Cost for Session (₹)")}</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder={t("e.g. 1500")}
                                        value={completionData.treatment_cost}
                                        onChange={(e) => setCompletionData({ ...completionData, treatment_cost: e.target.value })}
                                    />
                                </div>
                            </Col>
                        )}

                        <Col md={isPaymentModuleEnabled() ? 6 : 12}>
                            <label className="form-label font-weight-semibold">{t("Next Visit / Recommended Follow-up")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("e.g. In 7 days for RCT Obturation, In 1 month for Review")}
                                value={completionData.next_visit}
                                onChange={(e) => setCompletionData({ ...completionData, next_visit: e.target.value })}
                            />
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter className="bg-light d-flex justify-content-between">
                    <Button color="secondary" onClick={() => setIsEndModalOpen(false)} disabled={saving}>
                        {t("Cancel")}
                    </Button>
                    <div className="d-flex gap-2">
                        <Button color="primary" onClick={() => handleEndSessionSubmit(false)} disabled={saving} className="d-flex align-items-center gap-1.5 font-weight-semibold shadow-sm">
                            <RiCheckboxCircleLine /> {saving ? t('Saving...') : t('Save & Finish Session')}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>

            {/* Quick Invoice Generation Modal (When amount was missing) */}
            <Modal isOpen={isInvoiceModalOpen} toggle={() => setIsInvoiceModalOpen(false)} centered size="md">
                <ModalHeader toggle={() => setIsInvoiceModalOpen(false)} className="bg-light">
                    <div className="d-flex align-items-center gap-2">
                        <RiBillLine className="text-primary font-size-20" />
                        <span className="font-weight-bold">{t("Generate Invoice for Session")}</span>
                    </div>
                </ModalHeader>

                <ModalBody className="p-4">
                    <div className="bg-light p-3 rounded mb-3 border">
                        <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted font-size-12">{t("Patient")}:</span>
                            <span className="font-weight-semibold">{patientData?.first_name} {patientData?.last_name} ({patientData?.case_no})</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="text-muted font-size-12">{t("Session Reference")}:</span>
                            <span className="font-weight-semibold">Operatory Chair {activeAppt?.chair_code || '1'} • #{activeAppt?.id || ''}</span>
                        </div>
                    </div>

                    <form onSubmit={handleSaveModalInvoice}>
                        <div className="mb-3">
                            <label className="form-label font-weight-semibold">{t("Procedure / Treatment Description *")}</label>
                            <input
                                type="text"
                                className="form-control"
                                required
                                placeholder={t("e.g. Scaling & Polishing, Class II Composite Filling")}
                                value={invoiceModalData.treatment_name}
                                onChange={(e) => setInvoiceModalData({ ...invoiceModalData, treatment_name: e.target.value })}
                            />
                        </div>

                        <Row className="g-2 mb-3">
                            <Col md={6}>
                                <label className="form-label font-weight-semibold">{t("Procedure Amount (₹) *")}</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        required
                                        min="1"
                                        placeholder="0"
                                        autoFocus
                                        value={invoiceModalData.cost}
                                        onChange={(e) => setInvoiceModalData({ ...invoiceModalData, cost: e.target.value })}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <label className="form-label font-weight-semibold">{t("Discount (₹)")}</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        min="0"
                                        placeholder="0"
                                        value={invoiceModalData.discount}
                                        onChange={(e) => setInvoiceModalData({ ...invoiceModalData, discount: e.target.value })}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row className="g-2 mb-3">
                            <Col md={6}>
                                <label className="form-label font-weight-semibold">{t("Amount Paid Now (₹)")}</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        min="0"
                                        placeholder="0"
                                        value={invoiceModalData.paid}
                                        onChange={(e) => setInvoiceModalData({ ...invoiceModalData, paid: e.target.value })}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <label className="form-label font-weight-semibold">{t("Payment Mode")}</label>
                                <select
                                    className="form-select"
                                    value={invoiceModalData.payment_mode}
                                    onChange={(e) => setInvoiceModalData({ ...invoiceModalData, payment_mode: e.target.value })}
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="UPI / QR">UPI / QR Code</option>
                                    <option value="Credit/Debit Card">Credit/Debit Card</option>
                                    <option value="Net Banking">Net Banking</option>
                                </select>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <label className="form-label font-weight-semibold">{t("Invoice Remarks")}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("Optional remarks or invoice notes")}
                                value={invoiceModalData.remark}
                                onChange={(e) => setInvoiceModalData({ ...invoiceModalData, remark: e.target.value })}
                            />
                        </div>

                        {/* Live Financial Calculation Summary */}
                        {parseFloat(invoiceModalData.cost || 0) > 0 && (
                            <div className="p-3 bg-light rounded border mb-2 font-size-13">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-muted">{t("Total Payable")}:</span>
                                    <strong>₹{Math.max(0, (parseFloat(invoiceModalData.cost) || 0) - (parseFloat(invoiceModalData.discount) || 0)).toLocaleString('en-IN')}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-muted">{t("Paid Amount")}:</span>
                                    <span className="text-success font-weight-semibold">₹{(parseFloat(invoiceModalData.paid) || 0).toLocaleString('en-IN')}</span>
                                </div>
                                <div className="d-flex justify-content-between pt-1 border-top">
                                    <span className="text-muted">{t("Balance Due")}:</span>
                                    <span className="text-danger font-weight-bold">
                                        ₹{Math.max(0, ((parseFloat(invoiceModalData.cost) || 0) - (parseFloat(invoiceModalData.discount) || 0)) - (parseFloat(invoiceModalData.paid) || 0)).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            </div>
                        )}
                    </form>
                </ModalBody>

                <ModalFooter className="bg-light d-flex justify-content-between">
                    <Button color="secondary" onClick={() => setIsInvoiceModalOpen(false)} disabled={saving}>
                        {t("Cancel")}
                    </Button>
                    <Button color="primary" onClick={handleSaveModalInvoice} disabled={saving} className="d-flex align-items-center gap-1.5 font-weight-semibold shadow-sm">
                        <RiCheckLine className="font-size-16" /> {saving ? t('Generating...') : t('Generate & Create Invoice')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default LiveTreatmentBanner;
